import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppConfig } from '@app/core/app.config';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { RelatedAsset } from '@app/core/model/entities/asset/asset';
import { Space } from '@app/core/model/entities/asset/space';
import { Equipment, EquipmentInput } from '@app/core/model/entities/equipments/equipment';
import { Field, SECTION_ENTITY_TYPE_INJECTION } from '@app/core/model/other/field-config';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { SpacesService } from '@app/features/main/views/organization-spaces/spaces.service';
import { eq } from '@app/shared/extra/filter-model-builder';
import { getLocale, isNullOrEmpty } from '@app/shared/extra/utils';
import { ColumnBuilder } from '@app/shared/grid/column-builder';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import { GridOptionsService } from '@app/shared/grid/grid-options.service';
import { GeneratesObject } from '@app/shared/interfaces/generates-object';
import { ExtraValidators } from '@app/shared/validators/extra-validators.module';
import { TranslateService } from '@ngx-translate/core';
import { FieldService } from '@services/field.service';
import { AccessManager } from '@services/managers/access.manager';
import { GridApi, GridOptions, RowSelectedEvent } from 'ag-grid-community';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { filter, mergeMap, switchMap, toArray } from 'rxjs/operators';

@Component({
  templateUrl: './equipment-duplicate-modal.component.html',
  styleUrl: '../../../../../../../shared/components/form-builder/sections/create-modal.component.scss',
  standalone: false,
  providers: [
    {provide: SECTION_ENTITY_TYPE_INJECTION, useValue: EntityTypeEnum.EQUIPMENT}
  ]
})
export class EquipmentDuplicateModalComponent implements OnInit, OnDestroy, GeneratesObject {

  protected readonly EntityTypeEnum = EntityTypeEnum;

  // Ag-grid
  public gridApi: GridApi;
  public gridOptions: GridOptions;

  protected modalTitle: string;
  public spaces = new BehaviorSubject<Space[]>([]);
  protected equipmentForm: FormGroup<{
    name: FormControl<string>,
    asset: FormControl<RelatedAsset>,
    spaces: FormControl<Space[]>
  }>;
  protected assetTypeAllowsSpaceModule = false;

  public appConfig = inject(AppConfig);
  protected translate: TranslateService = inject(TranslateService);
  protected fb = inject(FormBuilder);
  protected assetsService = inject(AssetsService);
  protected spacesService = inject(SpacesService);
  protected columnBuilder = inject(ColumnBuilder);
  protected gridOptionsService = inject(GridOptionsService);
  private fieldService = inject(FieldService);
  private accessManager = inject(AccessManager);
  protected destroyRef = inject(DestroyRef);

  protected missingRequiredFields$: Observable<Field[]>;

  private invalidRows: string[] = [];

  constructor() {
    const data = inject<{ equipments: Equipment[] }>(MAT_DIALOG_DATA);

    this.gridOptions = {
      ...this.gridOptionsService.staticSelectSpacesGridOptions,
      defaultColDef: {
        resizable: true,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        filter: true
      },
      treeData: true,
      localeText: this.columnBuilder.localeTextCommon(),
      multiSortKey: this.appConfig.GRID_MULTI_SORT_KEY,
      domLayout: 'autoHeight',
      rowClassRules: {
        'invalid-row': ({data}: { data: Space }): boolean => this.invalidRows.includes(data.id)
      },
      onRowClicked: this.onRowSelected.bind(this),
      onRowSelected: this.onRowSelected.bind(this)
    };
    this.equipmentForm = this.fb.group({
      name: this.fb.control(this.translate.instant('VALUE.NEW_EQUIPMENT'), Validators.compose([
        Validators.required,
        Validators.maxLength(this.appConfig.FIELD_MAX_LENGTH)
      ])),
      asset: this.fb.control(void 0, Validators.compose([
        Validators.required
      ])),
      spaces: this.fb.control([], ExtraValidators.checkSpaces)
    });
    this.modalTitle = data.equipments.length > 1 ? 'TITLE.DUPLICATE_EQUIPMENTS' : 'TITLE.DUPLICATE_EQUIPMENT';
    this.equipmentForm.controls.name.setValue(data.equipments[0].name);

    // All fields with a value in all selected equipments
    const equipmentsExistingFields = data.equipments.flatMap(equipment =>
      Object.entries(equipment)
        .filter(([_, value]) => !isNullOrEmpty(value))
        .map(([key]) => key)
        .concat(Object.keys(equipment.properties))
    );

    this.missingRequiredFields$ = this.fieldService.getFields({computed: 'ALL', entityType: EntityTypeEnum.EQUIPMENT}).pipe(
      mergeMap(fields => from(fields)),
      // Keep fields that exist in all equipments
      filter(field => !field.computed && equipmentsExistingFields.includes(field.code)),
      // Keep remaining fields without sufficient user permissions
      filter(({code}) => !this.accessManager.hasFieldWriteOnCreateAccess(code, EntityTypeEnum.EQUIPMENT)),
      toArray(),
      takeUntilDestroyed()
    );
  }

  private onRowSelected(event: RowSelectedEvent): void {
    const spaces = this.gridApi.getSelectedRows() as Space[];
    this.equipmentForm.controls.spaces.setValue(spaces);
    this.equipmentForm.controls.spaces.markAsTouched();
    const nodesToRefresh = [event.node];
    // Check if row is invalid and update invalid rows
    if (event.node.isSelected() && (this.hasParentSelected(event.node.data) || this.hasChildrenSelected(event.node.data))) {
      this.invalidRows.push(event.node.data.id);
    } else if (!event.node.isSelected()) {
      this.invalidRows = this.invalidRows.filter(row => {
        const rowNode = this.gridApi.getRowNode(row);
        nodesToRefresh.push(rowNode);
        return row !== event.node.data.id
          && (this.hasParentSelected(rowNode.data) || this.hasChildrenSelected(rowNode.data));
      });
    }
    // Refresh rows
    this.gridApi.redrawRows({
      rowNodes: nodesToRefresh
    });
  }

  /**
   * Checks if a Space has ancestors that are selected in the grid.
   * @param space Space to check.
   * @return True if at least one ancestor is selected, false otherwise.
   * @private
   */
  private hasParentSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows()
      .some(selectedRow => space.parentPath.includes(selectedRow.id));
  }

  /**
   * Checks if a Space has children that are selected in the grid.
   * @param space Space to check.
   * @return True is at least one child is selected, false otherwise.
   * @private
   */
  private hasChildrenSelected(space: Space): boolean {
    return this.gridApi.getSelectedRows()
      .some(selectedRow => selectedRow.parentPath.includes(space.id));
  }

  /**
   * Load the grid's column definitions and fill with the selected Asset's Spaces.
   * @param params Ag-grid params.
   */
  public onGridReady(params): void {
    this.gridApi = params.api;
    this.gridApi.applyColumnState({
      state: [
        {colId: 'ag-Grid-AutoColumn', sort: 'asc'}
      ]
    });

    // Resize columns when receiving new Spaces
    this.spaces.subscribe(spaces => {
      this.gridApi.setGridOption('rowData', spaces);
      this.gridApi.sizeColumnsToFit();
      this.equipmentForm.controls.spaces.reset([]);
    });
  }

  /* Data from the filled form.
  * @return Equipment data.
  */
  public getGeneratedObject(): EquipmentInput {
    const {asset, spaces} = this.equipmentForm.getRawValue();
    const spaceIds = spaces.map(space => space.id);
    return {
      assetId: asset?.id,
      spaceIds
    };
  }

  public ngOnInit(): void {
    // Listen for Spaces to be displayed in the grid
    this.equipmentForm.controls.asset.valueChanges
      .pipe(
        switchMap(relatedAsset => {
          this.assetTypeAllowsSpaceModule = relatedAsset?.typeModules.includes(EntityTypeEnum.SPACE);
          if (this.assetTypeAllowsSpaceModule) {
            this.equipmentForm.controls.spaces.addValidators(Validators.required);
            const selectParams: SelectParams = {
              locale: getLocale(),
              filters: [
                eq('assetId', relatedAsset.id)
              ]
            };
            return this.spacesService.loadAllSpaces(selectParams);
          }
          this.equipmentForm.controls.spaces.removeValidators(Validators.required);
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.spaces);
  }

  /**
   * Stop Observable subscriptions.
   */
  public ngOnDestroy(): void {
    this.spaces.complete();
  }
}
